import { ReactNode } from 'react';

declare global {
  interface Window {
    PayPalButton: any;
    PaymentButton: any;
    SecureFields: any;
  }
}

type PaymentBoxProps = {
  id: string;
  datatransSecureFieldUrl: string;
  datatransPaymentButtonUrl: string;
  datatransPayPalUrl: string;
  merchantIdBooking: string;
  gpayMerchantId: string;
  campaignIdPaymentNotification: string;
  title: string;
  subtitle: string;
  paymentButton: string;
  paymentButtonAriaLabel: string;
  details: string;
  cardNoLabel: string;
  expirationLabel: string;
  cvvLabel: string;
  active: boolean;
  type: PaymentType;
  price: string;
  mandatorylabel: string;
  termsLabel: string;
  disclaimer: string;
  visaImg: string;
  mastercardImg: string;
  amexImg: string;
  paypalImg: string;
  googleImg: string;
  appleImg: string;
  klarnaImg: string;
  dinersImg: string;
  cardImg: string;
  paymentMethodAriaLabels?: PaymentMethodAriaLabel[];
  onPaymentMethodChange: (val: PaymentType) => void;
  expirationDateLabel?: string;
  isDisabled?: boolean;
  /** Klarna placement for checkout: 'klarnaWithImport' (credit-promotion-auto-size), 'klarnaWithoutImport' (top-strip-promotion-auto-size), 'none' (hidden) */
  selectKlarnaPlacementCheckout?: string;
  /** Show Klarna branded CTA button (with logo) instead of generic "Place order" when Klarna is selected */
  klarnaBrandedCTA?: boolean;
  /** Label for Klarna branded CTA button (when klarnaBrandedCTA is true). Falls back to paymentButton if not set. */
  klarnaOrderButtonLabel?: string;
  /** Whether card authorization checkbox is enabled (CMS-driven) */
  cardAuthorization?: boolean;
  /** Label for card authorization checkbox (CMS-driven) */
  cardAuthorizationLabel?: string;
  /** Description/subtext for card authorization checkbox (CMS-driven) */
  cardAuthorizationDescription?: string;
  /** ARIA label for card authorization checkbox (CMS-driven) */
  cardAuthorizationAriaLabel?: string;
};

type PaymentCardProps = {
  datatransSecureFieldUrl: string;
  datatransPaymentButtonUrl: string;
  datatransPayPalUrl: string;
  merchantIdBooking: string;
  gpayMerchantId: string;
  campaignIdPaymentNotification: string;
  cardLabel: string;
  cvvLabel: string;
  detailsLabel: string;
  orderButtonLabel: string;
  orderButtonAriaLabel: string;
  type: PaymentType;
  mandatorylabel: string;
  disclaimer: string;
  termsLabel: string;
  visaImg: string;
  mastercardImg: string;
  amexImg: string;
  paypalImg: string;
  //googleAppleImg: string;
  googleImg: string;
  appleImg: string;
  klarnaImg: string;
  dinersImg: string;
  cardImg: string;
  expirationLabel: string;
  toasterCloseOnAction?: boolean;
  toasterCloseOnX?: boolean;
  expirationDateLabel?: string;
  /** Klarna placement for checkout: 'klarnaWithImport' (credit-promotion-auto-size), 'klarnaWithoutImport' (top-strip-promotion-auto-size), 'none' (hidden) */
  selectKlarnaPlacementCheckout?: string;
  /** Show Klarna branded CTA button (with logo) instead of generic "Place order" when Klarna is selected */
  klarnaBrandedCTA?: boolean;
  /** Label for Klarna branded CTA button (when klarnaBrandedCTA is true). Falls back to orderButtonLabel if not set. */
  klarnaOrderButtonLabel?: string;
  /** Whether card authorization checkbox is enabled (CMS-driven) */
  cardAuthorization?: boolean;
  /** Label for card authorization checkbox (CMS-driven) */
  cardAuthorizationLabel?: string;
  /** Description/subtext for card authorization checkbox (CMS-driven) */
  cardAuthorizationDescription?: string;
  /** ARIA label for card authorization checkbox (CMS-driven) */
  cardAuthorizationAriaLabel?: string;
  paymentMethodAriaLabels?: PaymentMethodAriaLabel[];
};

type PaymentMethodAriaLabel = {
  paymentMethodId: string;
  ariaLabel: string;
};

type PaymentCardItemProps = {
  type: string;
  id: string;
  images: string[];
  checked: boolean;
  onTypeClick: (id: string) => void;
  ariaLabel?: string;
};

type PaymentSubmitProps = {
  id: string;
  terms: string;
  onCheck: ((event: any) => void) | undefined;
  checked: boolean;
  children: ReactNode;
  onSubmit?: React.MouseEventHandler<HTMLButtonElement>;
  cardAuthorization?: boolean;
  cardAuthorizationLabel?: string;
  cardAuthorizationDescription?: string;
  cardAuthorizationAriaLabel?: string;
  isCardAuthChecked?: boolean;
  onCardAuthChange?: (event: any) => void;
};

type SecureFieldProps = {
  amountType: string;
  fieldType: string;
  label: string;
  customClass: string;
  callback: () => any;
  children?: ReactNode;
};

type SecureFieldsProps = {
  paymentType: string;
  secureFields: any;
  config: {
    paymentType: string;
    campaignId: string;
    merchantID: string;
    totalAmount: string;
    transactionId: string;
    fields: {
      cardNumber: FieldConfig;
      cvv: FieldConfig;
    };
    labels: {
      cardLabel: string;
      cvvLabel: string;
      orderButtonLabel: string;
      orderButtonAriaLabel: string;
      mandatorylabel: string;
      disclaimer: string;
      termsLabel: string;
      expirationLabel: string;
      toasterCloseOnAction?: boolean;
      toasterCloseOnX?: boolean;
    };
    options: Record<string, unknown>;
    cardImg?: string;
  };
  onSecureFieldsError: (
    type: 'otpLoading' | 'otpError' | 'datatransError',
    value: boolean | string
  ) => void;
  expirationDateLabel?: string;
  /** Whether card authorization checkbox is enabled (CMS-driven) */
  cardAuthorization?: boolean;
  /** Label for card authorization checkbox (CMS-driven) */
  cardAuthorizationLabel?: string;
  /** Description/subtext for card authorization checkbox (CMS-driven) */
  cardAuthorizationDescription?: string;
  /** ARIA label for card authorization checkbox (CMS-driven) */
  cardAuthorizationAriaLabel?: string;
};

type FieldConfig = {
  placeholderElementId: string;
  inputType: string;
  placeholder: string;
  ariaLabel?: string;
  iframeTitle?: string;
};

type PaymentDetails = {
  label: string;
  amount: { value: string; currency: string };
};

export interface AppleGooglePlayTypes {
  merchantId: string;
  datatransPaymentButtonUrl: string;
  gpayMerchantId: string;
  merchantName: string;
  options: {
    allowedCardNetworks: Array<string>;
    useGooglePay: boolean;
    useApplePay: boolean;
  };
  payment: {
    // W3C spec
    details: {
      total: PaymentDetails;
    };
    // W3C spec
    options: {
      requestPayerEmail: boolean;
      requestPayerName: boolean;
      requestPayerPhone: boolean;
    };
    // Datatrans specific
    transaction: {
      countryCode: string;
      refno: string;
    };
  };
  paymentType: string;
}

export interface PaymentButtonProps extends AppleGooglePlayTypes {
  datatransPaymentButtonUrl: string;
  paymentType: string;
  termsLabel: string;
  toasterCloseOnX?: boolean;
  toasterCloseOnAction?: boolean;
}

type PayPalButtonProps = {
  datatransPayPalUrl: string;
  merchantIdBooking: string;
  paymentType: string;
  termsLabel: string;
  transactionId: string;
  amount: string;
};

type PayPalHookProps = {
  datatransPayPalUrl: string;
  merchantIdBooking: string;
  paymentType: string;
  transactionId: string;
  amount: string;
};

export enum PaymentType {
  TOTALTRAVEL = 'totaltravelfare',
  DEPOSIT = 'deposit',
  INSTALLMENTS = 'installments',
}

// Values should be the same with BookingFunnelConfig.java
export enum PaymentMethod {
  SECURE_FIELDS = 'securefields',
  PAYPAL = 'paypal',
  GOOGLE_APPLE_PAY = 'googleapplepay',
  //GOOGLE_PAY = 'googlepay',
  //APPLE_PAY = 'applepay',
  KLARNA = 'klarna',
  DINERS = 'diners',
}

// Values should be the same with BookingFunnelConfig.java
export enum PaymentButtons {
  GOOGLE_PAY = 'googlepay',
  APPLE_PAY = 'applepay',
}

export type Payment = {
  type: PaymentType;
  method: string;
  active: boolean;
};

type Warning = {
  dateTime: string;
  errorId: string;
  code: string;
  message: string;
  source: {
    name: string;
    layer: string;
  };
};

type BookingResult = {
  bookingType: string;
  bookingNumber: number;
  totalPaymentReceived: string;
  warnings?: Warning[];
  savePaymentMethod?: boolean;
};

export type {
  PaymentBoxProps,
  PaymentCardProps,
  PaymentCardItemProps,
  PaymentMethodAriaLabel,
  PaymentSubmitProps,
  PayPalButtonProps,
  SecureFieldProps,
  SecureFieldsProps,
  PayPalHookProps,
  BookingResult,
};
